/**
 * Methods for manipulating the question activities state
 */
import { Map, Iterable } from 'immutable';

import {
    checkQuestion,
    setNumericAnswer,
    setExpressionAnswer,
    selectCard,
    clearSelectedCard,
    dragSelectedCard,
    selectChoice,
    toggleChoice,
} from 'phoenix2/utils/question';

/**
 * String values for the evaluation status of a question
 */
export const EVALUATIONS = {
    correct: 'correct',
    wrong: 'wrong',
    error: 'error',
};

/**
 * Get the question activity with the given id from the question activity state
 * @param questionActivityState the current question activity state
 * @param attemptId the id of the question activity attempt to retrieve
 * @returns {any|*|T} An immutable map
 */
export const getQuestionActivity = (questionActivityState, attemptId) =>
    questionActivityState.getIn(['activities', attemptId]);

/**
 * Set the question activity with the given id into the question activity state
 * @param questionActivityState the question activities state
 * @param attemptId the id of the question activity attempt
 * @param questionActivity the question activity (Immutable Map)
 * @returns the updated question activity state
 */
export const setQuestionActivity = (questionActivityState, attemptId, questionActivity) =>
    questionActivityState.setIn(['activities', attemptId], questionActivity);

/**
 * Update a questionActivity to determine if it is valid for answer submission
 * @param questionActivity the question activity (Immutable Map)
 * @returns the question activity after being update with a validation
 * (Immutable Map)
 */
const _validateQuestionActivity = questionActivity => {
    const problemClass = questionActivity.getIn(['info', 'pc']);
    const valid = checkQuestion(problemClass);
    return questionActivity.setIn(['info', 'pc', 'valid'], valid);
};

const inputPath = step => ['info', 'pc', 'steps', step, 'input'];

/**
 * Update the input for the given step number of a question activity by
 * applying an update function to it, and then updating the question
 * activity with a validation
 * @param qa the question activity
 * @param step number of the question step
 * @param updater function to update the input
 * @private
 */
const _updateInputForStep = (qa, step, updater) =>
    _validateQuestionActivity(qa.updateIn(inputPath(step), updater));

/**
 * Update a question activity with a numeric answer by adding the answer to
 * the input of the given number field for the given step number
 * @param questionActivity
 * @param stepNumber
 * @param numberFieldId
 * @param answer
 */
export const setNumericAnswerToQuestionActivityInput = (
    questionActivity,
    stepNumber,
    numberFieldId,
    answer,
) =>
    _updateInputForStep(questionActivity, stepNumber, input =>
        setNumericAnswer(input, numberFieldId, answer),
    );

/**
 * Update a question activity with a expression answer by adding the answer to
 * the input of the given number field for the given step number
 * @param questionActivity
 * @param stepNumber
 * @param numberFieldId
 * @param answer
 */
export const setExpressionAnswerToQuestionActivityInput = (
    questionActivity,
    stepNumber,
    numberFieldId,
    answer,
) =>
    _updateInputForStep(questionActivity, stepNumber, input =>
        setExpressionAnswer(input, numberFieldId, answer),
    );

/**
/**
 * Update a question activity to record that the user has selected an answer
 * card
 * @param questionActivity
 * @param stepNumber
 * @param cardId
 */
export const selectCardForQuestionActivityStep = (questionActivity, stepNumber, cardId) =>
    _updateInputForStep(questionActivity, stepNumber, input => selectCard(input, cardId));

export const clearSelectedCardForQuestionActivityStep = (questionActivity, stepNumber) =>
    _updateInputForStep(questionActivity, stepNumber, input => clearSelectedCard(input));

/**
 * Update a question activity to record that the user has dragged an answer
 * option into a slot
 * @param questionActivity
 * @param stepNumber
 * @param slotId
 */
export const dragSelectedCardForQuestionActivityStep = (questionActivity, stepNumber, slotId) =>
    _updateInputForStep(questionActivity, stepNumber, input => dragSelectedCard(input, slotId));

export const selectChoiceForQuestionActivityStep = (
    questionActivity,
    stepNumber,
    choiceId,
    selected = undefined,
) =>
    _updateInputForStep(questionActivity, stepNumber, input =>
        selectChoice(input, choiceId, selected),
    );

/**
 * Update a question activity to record that the user has toggled an answer
 * choice
 * @param questionActivity
 * @param stepNumber
 * @param choiceId
 */
export const toggleChoiceForQuestionActivityStep = (questionActivity, stepNumber, choiceId) =>
    _updateInputForStep(questionActivity, stepNumber, input => toggleChoice(input, choiceId));

const updateStyles = (input, styleUpdates) =>
    input.update('styles', Map(), styles =>
        styles.mergeWith((prev, next) => Math.min(prev, next), styleUpdates),
    );

/**
 * Update a question activity to record a new styling of a question activity
 * input - e.g. if the window size has changed
 * @param questionActivity
 * @param stepNumber
 * @param styles
 */
export const setStylesForQuestionActivityStep = (questionActivity, stepNumber, styles) =>
    _updateInputForStep(questionActivity, stepNumber, input => updateStyles(input, styles));

/**
 * Preprocesses a question activity for rendering
 *
 * @param {*} questionActivity the question activity
 */
export const preprocessQuestion = questionActivity => {
    const stepsPath = ['info', 'pc', 'steps'];
    const steps = questionActivity.getIn(stepsPath);

    questionActivity = questionActivity.setIn(
        stepsPath,
        steps.map(step => processStep(step)),
    );
    return questionActivity;
};

/**
 * Exported only for testing purposes
 *
 * Performs transformation steps on a single step
 *
 * @param step the step of the question
 * @param random parametrised for testing to allow an alternate random source
 * @returns the transformed step
 */
export const processStep = (step, random = Math.random) => {
    step = processInputGroups(step, random);
    step = step.set('layout', updateLayout(step.get('layout'), step.get('input')));
    return step;
};

// Does processing on individual input groups, including ordering tiles
const processInputGroups = (step, random = Math.random) => {
    // ref_lookup contains a map of ref => group path that
    // the answer renderer can use to get properties about
    // the card/choice groups
    step = step.setIn(['input', 'ref_lookup'], new Map());
    step = processChoiceGroups(step, random);
    step = processCardGroups(step, random);
    return step;
};

const processChoiceGroups = (step, random = Math.random) =>
    processGroups(step, 'choice_groups', 'choice_refs', random);

const processCardGroups = (step, random = Math.random) =>
    processGroups(step, 'card_groups', 'card_refs', random);

const processGroups = (step, group_field, ref_field, random = Math.random) => {
    let groups = step.getIn(['input', group_field]);
    let refs = Map();
    groups = groups.map((grp, id) => {
        const grefs = grp.get(ref_field);
        grefs.forEach(r => {
            // For each card/choice reference, add the lookup to find its group
            // Assumption: references are unique in the step (even across groups)
            refs = refs.set(r, [group_field, id]);
        });

        if (grp.get('shuffle')) {
            // Shuffle references in input where appropriate
            grp = grp.set(ref_field, grefs.sortBy(random));
        }

        return grp;
    });

    step = step
        // Replace the group field
        .setIn(['input', group_field], groups)
        // Extend the ref lookup map with this group
        .mergeIn(['input', 'ref_lookup'], refs);
    return step;
};

/** updateLayout recursively looks through the layout for elements containing
 * groups of references, and matches their order with the input.
 * We also attach a group lookup to the data model for the rendered
 * components to use
 */
const updateLayout = (elem, input) => {
    if (!Iterable.isIterable(elem)) {
        return elem;
    }

    // This should be == null, but the linter hates it
    if (!elem.get('content')) {
        return elem;
    }

    const type = elem.get('type');
    if (type) {
        if (type.contains('cards')) {
            return updateCardLayout(elem, input);
        }

        if (type.contains('choices')) {
            return updateChoiceLayout(elem, input);
        }
    }

    // let recursion = recusion...
    let content = elem.get('content');
    content = content.map(e => updateLayout(e, input));
    return elem.set('content', content);
};

const updateCardLayout = (elem, input) => {
    const lookup = input.get('ref_lookup');

    let content = elem.get('content');
    const ref = content.getIn([0, 'ref']);
    if (!ref) {
        // This would occur with 0 length content
        return elem;
    }

    const gLookup = lookup.get(ref);
    const refs = input.getIn(gLookup).get('card_refs');
    // Use the order of refs to order the cards
    content = content.sortBy(e => refs.indexOf(e.get('ref')));
    // Replace the content and add the group_ref
    return elem.set('content', content).set('group_ref', gLookup);
};

const updateChoiceLayout = (elem, input) => {
    const lookup = input.get('ref_lookup');

    let content = elem.get('content');
    const ref = content.getIn([0, 'ref']);
    if (!ref) {
        // This would occur with 0 length content
        return elem;
    }

    const gLookup = lookup.get(ref);
    const refs = input.getIn(gLookup).get('choice_refs');
    // Use the order of refs to order the choices
    content = content.sortBy(e => refs.indexOf(e.get('ref')));
    // Replace the content and add the group_ref
    return elem.set('content', content).set('group_ref', gLookup);
};

export function parseQuestionActivityID(questionActivityID) {
    const [activityIndex, questionIndex] = questionActivityID.split('/');
    return {
        activityIndex,
        questionIndex: parseInt(questionIndex),
    };
}
